import React, { useCallback, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import axios from 'axios';
import config from '../config';
import './majApp.css';

/*
  MISE À JOUR IN-APP DE L'APK (option A validée par le proprio, 18/07).

  L'app n'est PAS sur le Play Store : elle est installée à la main. Ce composant
  fabrique la mise à jour que le Play Store aurait faite automatiquement.

  À l'ouverture (et au retour d'arrière-plan), sur MOBILE uniquement :
   1. lit sa propre version (App.getInfo) ;
   2. demande le manifeste /api/app/version (dernière, minimale, lien, notes) ;
   3. compare :
        version < minimale  -> écran BLOQUANT « mise à jour requise » (non fermable) ;
        minimale ≤ version < dernière -> bandeau OPTIONNEL (le livreur peut l'ignorer) ;
        version ≥ dernière -> rien.
   4. « Mettre à jour » ouvre le lien de l'APK dans le navigateur : Android la
      télécharge, l'utilisateur tape dessus, elle s'installe par-dessus (même
      signature, aucune donnée perdue).

  GARDE-FOU CAPITAL — fail-open : si la vérification échoue (serveur injoignable,
  réseau coupé, réponse illisible), on NE BLOQUE PAS. Un incident serveur ne doit
  jamais verrouiller la boutique un matin. Le blocage n'arrive QUE si le manifeste
  répond clairement « ta version est sous le minimum ».

  Web : rien. Le navigateur charge toujours le dernier déploiement.
*/

const DOMAIN = config.REACT_APP_SERVER_DOMAIN;

// Compare deux versions « 1.17.0 ». Renvoie <0, 0, >0. Tolérant (parties
// manquantes = 0, non numériques ignorées).
function comparerVersions(a, b) {
  const pa = String(a || '0').split('.').map((n) => parseInt(n, 10) || 0);
  const pb = String(b || '0').split('.').map((n) => parseInt(n, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const d = (pa[i] || 0) - (pb[i] || 0);
    if (d !== 0) return d < 0 ? -1 : 1;
  }
  return 0;
}

export default function MajApp() {
  const [etat, setEtat] = useState(null);   // null | 'bloquant' | 'optionnel'
  const [info, setInfo] = useState(null);   // { derniere, minimale, url, notes }
  const [reporte, setReporte] = useState(false); // bandeau optionnel masqué cette session

  const verifier = useCallback(async () => {
    if (!Capacitor.isNativePlatform()) return;
    try {
      const infos = await CapApp.getInfo();       // { version, build, ... }
      const version = infos?.version || '0.0.0';
      const { data } = await axios.get(`${DOMAIN}/api/app/version`, { timeout: 8000 });
      if (!data || !data.derniere) return;         // manifeste vide -> rien
      setInfo(data);
      if (comparerVersions(version, data.minimale) < 0) {
        setEtat('bloquant');
      } else if (comparerVersions(version, data.derniere) < 0) {
        setEtat('optionnel');
      } else {
        setEtat(null);
      }
    } catch (e) {
      // FAIL-OPEN : on ne bloque jamais sur une erreur de vérification.
      setEtat(null);
    }
  }, []);

  useEffect(() => {
    verifier();
    // Re-vérifie quand l'app revient au premier plan (le proprio a pu, entre-temps,
    // relever la version minimale).
    let sub;
    if (Capacitor.isNativePlatform()) {
      sub = CapApp.addListener('appStateChange', ({ isActive }) => { if (isActive) verifier(); });
    }
    return () => { if (sub && sub.remove) sub.remove(); };
  }, [verifier]);

  const mettreAJour = () => {
    if (!info?.url) return;
    // URL relative (« /uploads/apk/... ») -> absolue. Ouverture dans le
    // navigateur système : c'est lui qui télécharge l'APK et lance l'installeur.
    const url = /^https?:\/\//i.test(info.url) ? info.url : `${DOMAIN}${info.url}`;
    window.open(url, '_system');
  };

  if (etat === 'bloquant') {
    return (
      <div className="maj-overlay" role="dialog" aria-modal="true">
        <div className="maj-boite">
          <div className="maj-logo" aria-hidden="true">👟</div>
          <h2 className="maj-titre">Mise à jour requise</h2>
          <p className="maj-texte">
            Une nouvelle version de l'application Ndoé Boutique est nécessaire pour continuer.
            {info?.notes ? <><br /><span className="maj-notes">{info.notes}</span></> : null}
          </p>
          <button type="button" className="maj-bouton maj-bouton--plein" onClick={mettreAJour}>
            Mettre à jour maintenant
          </button>
          <p className="maj-aide">
            Le téléchargement s'ouvre dans votre navigateur. Ouvrez le fichier une fois
            téléchargé : la mise à jour s'installe par-dessus, sans rien perdre.
          </p>
        </div>
      </div>
    );
  }

  if (etat === 'optionnel' && !reporte) {
    return (
      <div className="maj-bandeau" role="status">
        <div className="maj-bandeau-texte">
          <b>Nouvelle version disponible{info?.derniere ? ` (v${info.derniere})` : ''}</b>
          {info?.notes ? <span className="maj-bandeau-notes">{info.notes}</span> : null}
        </div>
        <div className="maj-bandeau-actions">
          <button type="button" className="maj-bouton maj-bouton--plein maj-bouton--petit" onClick={mettreAJour}>
            Mettre à jour
          </button>
          <button type="button" className="maj-bouton maj-bouton--discret maj-bouton--petit" onClick={() => setReporte(true)}>
            Plus tard
          </button>
        </div>
      </div>
    );
  }

  return null;
}
